import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material"
import NotificationsIcon from "@mui/icons-material/Notifications"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PaymentIcon from "@mui/icons-material/Payment"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import WarningIcon from "@mui/icons-material/Warning"
import InfoIcon from "@mui/icons-material/Info"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DoneAllIcon from "@mui/icons-material/DoneAll"

import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../store/realtimeChat/actions"
import {
  selectNotifications,
  selectUnreadNotificationCount,
} from "../../store/realtimeChat/reducer"

const NOTIFICATION_ICONS = {
  order: ShoppingCartIcon,
  payment: PaymentIcon,
  shipping: LocalShippingIcon,
  warning: WarningIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
}

const NOTIFICATION_COLORS = {
  order: { bg: "#dbeafe", color: "#3b82f6" },
  payment: { bg: "#d1fae5", color: "#10b981" },
  shipping: { bg: "#ffedd5", color: "#f97316" },
  warning: { bg: "#fef3c7", color: "#d97706" },
  info: { bg: "#e0e7ff", color: "#6366f1" },
  success: { bg: "#d1fae5", color: "#059669" },
}

const NotificationBell = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const notifications = useSelector(selectNotifications)
  const unreadCount = useSelector(selectUnreadNotificationCount)
  const notificationsLoading = useSelector((state) => state.realtimeChat.notificationsLoading)

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Fetch notifications on mount
  useEffect(() => {
    dispatch(fetchNotifications())
  }, [dispatch])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      dispatch(markNotificationRead(notification.id))
    }

    // Navigate based on notification type
    if (notification.link) {
      navigate(notification.link)
      handleClose()
    }
  }

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead())
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return t("Just now")
    if (diffMins < 60) return t("{{count}} min ago", { count: diffMins })
    if (diffHours < 24) return t("{{count}} hour ago", { count: diffHours })
    if (diffDays < 7) return t("{{count}} day ago", { count: diffDays })
    return date.toLocaleDateString()
  }

  const getNotificationIcon = (type) => {
    const IconComponent = NOTIFICATION_ICONS[type] || InfoIcon
    const colors = NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.info
    return (
      <Avatar sx={{ bgcolor: colors.bg, color: colors.color, width: 36, height: 36 }}>
        <IconComponent sx={{ fontSize: 18 }} />
      </Avatar>
    )
  }

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        size="small"
        sx={{
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              height: 16,
              minWidth: 16,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsActiveIcon />
          ) : (
            <NotificationsIcon />
          )}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Notifications")}
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Stack>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {t("Mark all read")}
            </Button>
          )}
        </Box>

        {/* Notifications List */}
        {notificationsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <NotificationsIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("No notifications")}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 350, overflow: "auto" }}>
            {notifications.slice(0, 10).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  component="div"
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: "pointer",
                    bgcolor: notification.read ? "transparent" : "#f0f9ff",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={notification.read ? 400 : 600}
                        color="#1e293b"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="#64748b" noWrap sx={{ maxWidth: 180 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8">
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
                {index < notifications.slice(0, 10).length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 10 && (
          <Box sx={{ p: 1.5, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
            <Button
              size="small"
              onClick={() => {
                navigate("/notifications")
                handleClose()
              }}
              sx={{ textTransform: "none" }}
            >
              {t("View all notifications")}
            </Button>
          </Box>
        )}
      </Menu>
    </>
  )
}

export default NotificationBell
